import axios from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    const message = error.response?.data?.error || 'An error occurred';
    toast.error(message);
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
  
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },
  
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  
  updateProfile: async (profileData) => {
    const response = await api.put('/auth/profile', profileData);
    return response.data;
  }
};

// AI Tools API
export const aiAPI = {
  analyzeDocument: async (data) => {
    const response = await api.post('/ai/document-analysis', data);
    return response.data;
  },
  
  researchLegal: async (data) => {
    const response = await api.post('/ai/legal-research', data);
    return response.data;
  },
  
  summarizeCase: async (data) => {
    const response = await api.post('/ai/case-summary', data);
    return response.data;
  },
  
  reviewContract: async (data) => {
    const response = await api.post('/ai/contract-review', data);
    return response.data;
  },
  
  generateBrief: async (data) => {
    const response = await api.post('/ai/legal-brief', data);
    return response.data;
  },
  
  checkCompliance: async (data) => {
    const response = await api.post('/ai/compliance', data);
    return response.data;
  },
  
  findPrecedents: async (data) => {
    const response = await api.post('/ai/precedents', data);
    return response.data;
  },
  
  draftDocument: async (data) => {
    const response = await api.post('/ai/document-draft', data);
    return response.data;
  },
  
  estimateCost: async (data) => {
    const response = await api.post('/ai/cost-estimate', data);
    return response.data;
  }
};

// Worklist API
export const worklistAPI = {
  getWorkItems: async (params = {}) => {
    const response = await api.get('/worklist', { params });
    return response.data;
  },
  
  getWorkItem: async (id) => {
    const response = await api.get(`/worklist/${id}`);
    return response.data;
  },
  
  getRecentActivity: async () => {
    const response = await api.get('/worklist/recent/activity');
    return response.data;
  },
  
  getDashboardStats: async () => {
    const response = await api.get('/worklist/stats/dashboard');
    return response.data;
  },
  
  deleteWorkItem: async (id) => {
    const response = await api.delete(`/worklist/${id}`);
    return response.data;
  }
};

// Legal API
export const legalAPI = {
  getNews: async (params = {}) => {
    const response = await api.get('/legal/news', { params });
    return response.data;
  },
  
  getLegalData: async (language = 'en') => {
    const response = await api.get('/legal/data', { params: { language } });
    return response.data;
  },
  
  getLegalGuidance: async (data) => {
    const response = await api.post('/legal/guidance', data);
    return response.data;
  },
  
  getLegalGuidanceStream: async (data, onChunk, onComplete, onError) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/legal/guidance/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let buffer = '';
      let currentEvent = '';
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        
        for (const line of lines) {
          if (line.trim() === '') {
            // Empty line indicates end of event
            currentEvent = '';
            continue;
          }
          
          if (line.startsWith('event: ')) {
            currentEvent = line.substring(7).trim();
            continue;
          }
          
          if (line.startsWith('data: ')) {
            try {
              const eventData = JSON.parse(line.substring(6));
              
              switch (currentEvent) {
                case 'chunk':
                  onChunk?.(eventData);
                  break;
                case 'complete':
                case 'response': // Legacy support for "response" events
                  onComplete?.(eventData);
                  break;
                case 'error':
                  onError?.(eventData);
                  return; // Exit on error
                case 'done':
                  return; // Exit when done
                case 'start':
                  // Handle start event if needed
                  break;
                default:
                  console.log('Unknown SSE event:', currentEvent, eventData);
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e, line);
            }
          }
        }
      }
    } catch (error) {
      console.error('Streaming error:', error);
      onError?.({ error: error.message });
    }
  },
  
  translateContent: async (data) => {
    const response = await api.post('/legal/translate', data);
    return response.data;
  },
  
  updateLanguagePreference: async (language) => {
    const response = await api.put('/legal/language', { language });
    return response.data;
  },
  
  seedNews: async () => {
    const response = await api.post('/legal/seed-news');
    return response.data;
  }
};

export default api;
